import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaBrain, FaSave } from "react-icons/fa";

export default function EmotionalJournal({ userId, hideHeader }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getLocalISO(new Date()));
  const [tradesByDate, setTradesByDate] = useState({});
  const [dailyNote, setDailyNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Helper to get YYYY-MM-DD in local time
  function getLocalISO(dateObj) {
    const tzOffset = dateObj.getTimezoneOffset() * 60000;
    return new Date(dateObj.getTime() - tzOffset).toISOString().slice(0, 10);
  }

  // Fetch Trades Across ALL Accounts
  useEffect(() => {
    if (!userId) return;

    const loadAllTrades = async () => {
      setLoading(true);
      try {
        const accountsRef = collection(db, "users", userId, "accounts");
        const accountsSnap = await getDocs(accountsRef);
        
        const grouped = {};
        
        // Loop through every account the user owns
        for (const accDoc of accountsSnap.docs) {
          const accountData = accDoc.data();
          const accountName = accountData.name || accountData["Account Name"] || "Unknown Account";
          
          const journalRef = collection(db, "users", userId, "accounts", accDoc.id, "journal");
          const journalSnap = await getDocs(journalRef);
          
          journalSnap.forEach(tradeDoc => {
            const trade = tradeDoc.data();
            
            // 1. FILTER: Only include trades that actually have Emotional Notes
            const hasNotes = trade["Emotional Notes"] && String(trade["Emotional Notes"]).trim() !== "";
            if (!hasNotes) return;

            // 2. FILTER: Exclude copied trades (Follower trades)
            const isCopiedTrade = trade["Copied From Account"] || trade["Leader Trade #"] || trade["leaderTradeNum"];
            if (isCopiedTrade) return;

            // Group by Date
            const date = trade.Date?.slice(0, 10);
            if (!date) return;
            if (!grouped[date]) grouped[date] = [];
            
            // Attach the account name to the trade object for the UI
            grouped[date].push({ ...trade, id: tradeDoc.id, accountName });
          });
        }
        
        setTradesByDate(grouped);
      } catch (error) {
        console.error("Error fetching trades from all accounts:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAllTrades();
  }, [userId]);

  // Fetch the User-Level Daily Note for the Selected Date
  useEffect(() => {
    if (!userId || !selectedDate) return;

    const fetchDailyNote = async () => {
      try {
        // SAVED AT THE USER LEVEL, NOT THE ACCOUNT LEVEL
        const noteRef = doc(db, "users", userId, "emotional_journal", selectedDate);
        const snap = await getDoc(noteRef);
        if (snap.exists()) {
          setDailyNote(snap.data().note || "");
        } else {
          setDailyNote("");
        }
      } catch (error) {
        console.error("Error fetching daily note:", error);
      }
    };

    fetchDailyNote();
  }, [userId, selectedDate]);

  const handleSaveDailyNote = async () => {
    if (!userId || !selectedDate) return;
    setSaving(true);
    try {
      // SAVED AT THE USER LEVEL
      const noteRef = doc(db, "users", userId, "emotional_journal", selectedDate);
      await setDoc(noteRef, {
        date: selectedDate,
        note: dailyNote.trim(),
        updatedAt: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      console.error("Error saving daily note:", error);
      alert("Failed to save emotional journal entry.");
    } finally {
      setSaving(false);
    }
  };

  // Calendar Generation Logic
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  const handlePrevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const handleNextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const renderCalendarDays = () => {
    const blanks = Array.from({ length: firstDayOfMonth }, (_, i) => (
      <div key={`blank-${i}`} className="p-2"></div>
    ));

    const days = Array.from({ length: daysInMonth }, (_, i) => {
      const dayNum = i + 1;
      const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
      const isSelected = selectedDate === dateString;
      const hasTrades = tradesByDate[dateString] && tradesByDate[dateString].length > 0;
      const isToday = dateString === getLocalISO(new Date());

      return (
        <button
          key={dayNum}
          onClick={() => setSelectedDate(dateString)}
          className={`
            relative p-3 rounded-xl font-medium text-sm transition-all duration-200
            ${isSelected ? "bg-blue-600 text-white shadow-md" : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}
            ${isToday && !isSelected ? "ring-2 ring-blue-400" : ""}
          `}
        >
          {dayNum}
          {hasTrades && (
            <span className={`absolute bottom-1.5 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white" : "bg-blue-500"}`}></span>
          )}
        </button>
      );
    });

    return [...blanks, ...days];
  };

// Sort trades chronologically (First trade first)
  const selectedDayTrades = (tradesByDate[selectedDate] || []).sort((a, b) => {
    // 1. Primary Sort: By Trade #
    const tradeNumA = Number(a["Trade #"]);
    const tradeNumB = Number(b["Trade #"]);
    
    if (tradeNumA && tradeNumB && tradeNumA !== tradeNumB) {
      return tradeNumA - tradeNumB; // Ascending order
    }

    // 2. Secondary Sort: Fallback to Entry Time if Trade # is missing
    const parseTime = (timeStr) => {
      if (!timeStr) return 0;
      const match = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([APap][Mm])?/);
      if (!match) return 0;
      let h = parseInt(match[1], 10);
      const m = parseInt(match[2], 10);
      const ap = match[4];
      if (ap) {
        if (ap.toLowerCase() === 'pm' && h !== 12) h += 12;
        if (ap.toLowerCase() === 'am' && h === 12) h = 0;
      }
      return h * 60 + m;
    };
    return parseTime(a["Entry Time"]) - parseTime(b["Entry Time"]);
  });

return (
    <div className={`max-w-6xl mx-auto ${hideHeader ? 'w-full pb-8' : 'py-10 px-4 min-h-screen bg-gray-50 dark:bg-gray-900'}`}>
      
{/* Header - Conditionally rendered */}
      

        <div className="flex items-center gap-3 mb-8">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30">
            <FaBrain className="text-2xl text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-blue-900 dark:text-blue-200">
              Emotional Journal
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              Track your mindset, review trade emotions, and maintain psychological discipline.
            </p>
          </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Calendar */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <FaCalendarAlt className="text-blue-500" />
                {currentDate.toLocaleString("default", { month: "long", year: "numeric" })}
              </h2>
              <div className="flex gap-2">
                <button onClick={handlePrevMonth} className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition">
                  <FaChevronLeft size={12} />
                </button>
                <button onClick={handleNextMonth} className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition">
                  <FaChevronRight size={12} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-2 text-center mb-2">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(day => (
                <div key={day} className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-2 text-center">
              {renderCalendarDays()}
            </div>
          </div>
        </div>

        {/* Right Column: Selected Day Details */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Overall Daily Reflection */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-blue-900 dark:text-blue-200">
                Daily Reflection for {new Date(selectedDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
              </h3>
            </div>
            
            <textarea
              rows={5}
              value={dailyNote}
              onChange={(e) => setDailyNote(e.target.value)}
              placeholder="How were you feeling overall today? Were you focused, distracted, anxious, or calm?"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 bg-gray-50 dark:bg-gray-900 dark:text-white transition resize-y mb-4"
            />
            
            <div className="flex justify-end">
              <button
                onClick={handleSaveDailyNote}
                disabled={saving}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2.5 rounded-xl shadow transition disabled:opacity-50"
              >
                <FaSave />
                {saving ? "Saving..." : "Save Reflection"}
              </button>
            </div>
          </div>

          {/* Trade-Specific Emotional Notes */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-100 dark:border-gray-700 pb-2">
              Trade Emotion Notes
            </h3>

            {loading ? (
              <div className="text-center text-gray-500 py-8">Loading trades...</div>
            ) : selectedDayTrades.length === 0 ? (
              <div className="text-center text-gray-500 py-8 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                No emotional notes recorded for trades on this day.
              </div>
            ) : (
              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                {selectedDayTrades.map((trade, idx) => (
                  <div key={trade.id || idx} className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-bold text-blue-800 dark:text-blue-300">
                        {/* ✅ FORMAT PRODUCT NAME HERE */}
                        {trade.Product ? trade.Product.split(" - ")[0].trim() : "Unknown Asset"} 
                        <span className="text-xs text-gray-500 ml-2 font-normal">
                          Trade #{trade["Trade #"] || idx + 1} • <span className="italic">{trade.accountName}</span>
                        </span>
                      </div>
                      <div className={`px-2 py-0.5 rounded text-xs font-bold ${trade.Win ? "bg-green-100 text-green-700" : trade.Loss ? "bg-red-100 text-red-700" : "bg-gray-200 text-gray-700"}`}>
                        {trade.Win ? "WON" : trade.Loss ? "LOSS" : "DNT"}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                      <div>
                        <span className="block text-xs uppercase font-bold text-gray-400 mb-1">Mood / Mindset (1-5)</span>
                        <span className="text-gray-800 dark:text-gray-200 font-medium">
                          {trade["Mood_Mindset"] || trade["Mood"] || "Not recorded"}
                        </span>
                      </div>
                      <div>
                        <span className="block text-xs uppercase font-bold text-gray-400 mb-1">Followed Rules?</span>
                        <span className={`font-medium ${trade["Did I follow my Rules?"] === "Yes" ? "text-green-600" : trade["Did I follow my Rules?"] === "No" ? "text-red-500" : "text-gray-500"}`}>
                          {trade["Did I follow my Rules?"] || "Not recorded"}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <span className="block text-xs uppercase font-bold text-gray-400 mb-1">Emotional Notes</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {trade["Emotional Notes"]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}